/**
 * The download filename a server named in its Content-Disposition header.
 *
 * Prefers the RFC 5987 `filename*=UTF-8''…` form, which carries non-ASCII names intact, over the quoted
 * `filename="…"` ASCII fallback. The backend sends both, via Spring's `ContentDisposition`. It has to quote
 * the name: an unquoted comma (a client named "…SPE, LLC") makes Chrome reject the whole response, and the
 * service worker then reports that as a 504 (ISS-135).
 *
 * UTF-8 only: `decodeURIComponent` decodes UTF-8, and UTF-8 is the only charset the backend sends and the only
 * one RFC 6266 requires. A `filename*` in any other charset is ignored in favour of the plain parameter.
 */
export function filenameFromContentDisposition(header: string | null | undefined, fallback = 'download'): string {
  if (!header) {
    return fallback;
  }
  const params = parseParameters(header);
  const extended = /^([^']*)'[^']*'(.*)$/.exec(params.get('filename*') ?? '');
  if (extended && extended[1].trim().toLowerCase() === 'utf-8') {
    try {
      return decodeURIComponent(extended[2]);
    } catch {
      // Malformed percent-encoding; fall through to the plain parameter.
    }
  }
  return params.get('filename') || fallback;
}

/**
 * Splits `type; name=value; name="quoted; value"` into parameters, by name (lower-cased; first one wins).
 *
 * A tokenizer instead of a regex per parameter: a regex finds `filename=` inside `x-filename=`, or inside a
 * quoted value that happens to contain `filename*=`. An unquoted value runs to the next `;`, spaces and commas
 * included, so the legacy unquoted header still parses.
 */
function parseParameters(header: string): Map<string, string> {
  const params = new Map<string, string>();
  const firstSemi = header.indexOf(';');
  const firstEq = header.indexOf('=');
  // Skip the disposition type ("attachment") when there is one.
  let i = firstSemi >= 0 && (firstEq < 0 || firstSemi < firstEq) ? firstSemi : -1;
  while (i < header.length) {
    i++;
    const eq = header.indexOf('=', i);
    if (eq < 0) {
      break;
    }
    const semi = header.indexOf(';', i);
    if (semi >= 0 && semi < eq) {
      // A parameter with no value; skip it.
      i = semi;
      continue;
    }
    const name = header.slice(i, eq).trim().toLowerCase();
    let j = eq + 1;
    while (header[j] === ' ' || header[j] === '\t') {
      j++;
    }
    let value = '';
    if (header[j] === '"') {
      for (j++; j < header.length && header[j] !== '"'; j++) {
        if (header[j] === '\\' && j + 1 < header.length) {
          j++;
        }
        value += header[j];
      }
      const next = header.indexOf(';', j);
      i = next < 0 ? header.length : next;
    } else {
      const next = header.indexOf(';', j);
      const end = next < 0 ? header.length : next;
      value = header.slice(j, end).trim();
      i = end;
    }
    if (name && !params.has(name)) {
      params.set(name, value);
    }
  }
  return params;
}
